import { useMemo } from "react";
import { useApplicationContext } from "src/hooks/useApplication";
import { Message } from "../overviews/Message";
import { useEntityContext } from "../useEntityContext";
import { useEntityTabs } from "../components/TabbedEntityPage";
import { History, HistoryComponent } from "../History";
import { EntityScreen } from "./components/layout/Screen";
import { HeaderPanel } from "./components/layout/HeaderPanel";
import { ActionCard } from "./components/ActionCard";
import { SummaryTag } from "./components/Tags";
import { SummaryPanel } from "./components/layout/SummaryPanel";
import { InsightsPanel } from "./components/layout/InsightsPanel";
import { toHeaderAttributes } from "./components/HeaderAttributes";
import { TextMetric } from "./components/TextMetric";
import { EntityTabs } from "./components/EntityTabs";

export const MessageEntity = () => {
  const context = useEntityContext();
  const entity = context.dataRef.current.entity;
  const attributes = useMessageAttributes(entity);
  const { name, description, title } = entity.properties;
  return (
    <EntityScreen
      headerPanel={
        <HeaderPanel label={name} entity={entity} attributes={attributes} />
      }
      summaryPanel={
        <SummaryPanel title={name} description={description || title} />
      }
      insightsPanel={<InsightsPanel cards={useMessageInsights(entity)} />}
    >
      <EntityTabs
        propertiesSkipList={[
          "name",
          "description",
          "title",
          "vzblTone",
          "vzblEmotion",
          "vzblCategory",
          "vzblLanguage",
          "createdAt",
        ]}
        entity={entity}
        tabs={useEntityTabs({
          entity,
          others: [
            {
              name: "Message Overview",
              component: <Message entity={entity} />,
            },
          ],
          history: (
            <History>
              <HistoryComponent />
            </History>
          ),
        })}
      />
    </EntityScreen>
  );
};

export function useMessageInsights(entity: any) {
  const {
    vzblCategory: category,
    vzblEmotion: emotion,
    vzblTone: tone,
  } = entity.properties;

  return useMemo(
    () => [
      <ActionCard
        key="category"
        badge={<SummaryTag />}
        label="Category"
        value={<TextMetric text={category} />}
      />,
      <ActionCard
        key="tone"
        badge={<SummaryTag />}
        label="Tone"
        value={<TextMetric text={tone} />}
      />,
      <ActionCard
        key="emotion"
        badge={<SummaryTag />}
        label="Emotion"
        value={<TextMetric text={emotion} />}
      />,
    ],
    [category, tone, emotion]
  );
}

export function useMessageAttributes(entity: any) {
  const orgId = useApplicationContext().org_id!;
  const context = useEntityContext();
  const entityType = context.dataRef.current.entity.labels[0];
  const { createdAt, vzblLanguage } = entity.properties;
  return useMemo(
    () =>
      toHeaderAttributes({
        orgId,
        context,
        entityType,
        attributes: [
          {
            name: "created at",
            text: new Date(createdAt).toLocaleString(),
          },
          {
            name: "language",
            text: vzblLanguage,
          },
        ],
      }),
    [context, orgId, entityType, vzblLanguage, createdAt]
  );
}
